import {ActivityIndicator, Pressable, StyleSheet, Text, View} from "react-native";
import {useQuery} from "@tanstack/react-query";
import {ChatMessage, ChatPreview} from "@/lib/types";
import {dataRetryPolicy, fetchBackend} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {Color} from "@/lib/styles";
import ErrorMessage from "@/lib/components/ErrorMessage";
import Mosaic from "@/lib/components/Mosaic";
import {formatSince} from "@/lib/time";
import Icon from "@/lib/components/Icon";
import AvatarBox from "@/lib/components/AvatarBox";
import useCrossTabNavigation from "@/lib/crosstab";
import {useChat} from "@/lib/components/ChatProvider";

type Props = {
    chat_id: string,
    color: string,
}

export default function ChatPreviewBox({chat_id, color}: Props) {
    const {session} = useSession();
    const queryKey = ['chat', chat_id, session];
    const {navigate} = useCrossTabNavigation();
    const {subscribeToChat} = useChat();

    const {data, isLoading, error} = useQuery<ChatPreview>({
        queryKey,
        queryFn: () => fetchBackend(`/chat/${chat_id}`, "GET", session),
        retry: dataRetryPolicy,
    })

    if (isLoading || data === undefined) {
        return <ActivityIndicator size={"small"} color={Color.PrimaryText}/>
    } else if (error) {
        return <ErrorMessage error={error}/>
    }

    return (
        <Pressable style={[styles.container, {borderColor: color}]} onPress={() => {
            subscribeToChat(chat_id);
            navigate(`/chat/${chat_id}`);
        }}>
            <View style={styles.header}>
                <View>
                    <AvatarBox avatars={data.participants} numItems={5} color={Color.PrimaryText} border={false}/>
                </View>
                <View style={styles.stats}>
                    <View style={styles.statCounter}>
                        <Icon kind={"relation"} size={14}/>
                        <Text style={styles.statText}>{data.participants.length}</Text>
                    </View>
                    <View style={styles.statCounter}>
                        <Icon kind={"message"} size={14}/>
                        <Text style={styles.statText}>{data.num_replies}</Text>
                    </View>
                    <View style={styles.statCounter}>
                        <Icon kind={"camera"} size={14}/>
                        <Text style={styles.statText}>{data.num_thumbnails}</Text>
                    </View>
                </View>
            </View>
            <ChatMessagePresentation message={data.first_message}/>
            {data.num_truncated > 0 &&
                <View style={styles.messageBubble}>
                    <Text style={styles.truncatedMessages}>+ {data.num_truncated} svar</Text>
                </View>
            }
            {data.final_messages.map(msg => <ChatMessagePresentation key={msg.id} message={msg}/>)}
        </Pressable>
    );
}

type ChatMessageProps = {
    message: ChatMessage
}

function ChatMessagePresentation({message}: ChatMessageProps) {
    return (
        <View style={styles.messageBubble}>
            <View style={styles.messageHeader}>
                <View style={styles.sender}>
                    <Text style={[styles.senderGecos]}>{message.sender.gecos}</Text>
                </View>
                <View style={styles.timestamp}>
                    <Icon kind={"clock"} size={12} color={Color.PrimaryText}/>
                    <Text style={{fontSize: 12, color: Color.PrimaryText}}>{formatSince(new Date(message.received_at))}</Text>
                </View>
            </View>
            {message.text.length > 0 &&
                <Text style={[styles.text]}>{message.text}</Text>
            }
            <Mosaic thumbnailIds={message.thumbnail_ids} onPressThumbnail={() => {
            }}/>
        </View>
    );
}

export const styles = StyleSheet.create({
    container: {
        flexDirection: "column",
        gap: 1,
        backgroundColor: Color.Container,
        borderRadius: 8,
        borderWidth: 1,
        overflow: "hidden",
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        padding: 10,
        backgroundColor: Color.BackgroundContainer,
    },
    stats: {
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: 16,
    },
    statCounter: {
        gap: 4,
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
    },
    statText: {
        fontSize: 12,
        color: Color.PrimaryText,
    },
    messageBubble: {
        flexDirection: "column",
        flexWrap: "wrap",
        overflow: "hidden",
        backgroundColor: Color.BackgroundContainer,
    },
    messageHeader: {
        flexDirection: "row",
        flexWrap: "nowrap",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 10,
        gap: 16,
        width: "100%",
    },
    sender: {
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: 8,
        flexShrink: 1,
    },
    senderGecos: {
        fontSize: 16,
        fontWeight: 500,
        color: Color.PrimaryText,
        flexWrap: "wrap",
    },
    timestamp: {
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        gap: 4,
        fontSize: 12,
        color: Color.SecondaryText,
    },
    text: {
        flexWrap: "wrap",
        flexShrink: 1,
        fontSize: 16,
        padding: 10,
        paddingTop: 0,
        color: Color.PrimaryText,
    },
    truncatedMessages: {
        fontSize: 14,
        padding: 14,
        color: Color.PrimaryText,
    },
})